import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SessionService } from '@core/services/session.service';
import { StorageKey } from '@core/config/global.config';

export type DisplayDensity = 'compact' | 'comfortable' | 'wide';

const MAX_WIDTHS: Record<DisplayDensity, string> = {
  compact: '1280px',
  comfortable: '1600px',
  // A very large numeric value rather than the `none` keyword — behaves identically as a
  // max-width (nothing ever gets that wide), but stays a real length so the header's
  // full-bleed breakout math (_header.scss) can use it inside calc()/min().
  wide: '100vw',
};

/**
 * Persisted "how wide should the app content render" preference — mirrors the
 * language-switcher pattern (SessionService + apply-globally-on-change), but instead of
 * ngx-translate it drives a single CSS custom property, --detail-page-max-width, read by
 * `.layout-content-inner` (the wrapper around every routed page, see LayoutComponent) —
 * so it applies uniformly across the whole app, table pages included.
 */
@Injectable({
  providedIn: 'root',
})
export class DisplayDensityService {
  private sessionService = inject(SessionService);

  private density$: BehaviorSubject<DisplayDensity>;

  constructor() {
    const initial = this.readStoredDensity();
    this.density$ = new BehaviorSubject<DisplayDensity>(initial);
    this.apply(initial);
  }

  getDensity(): Observable<DisplayDensity> {
    return this.density$.asObservable();
  }

  setDensity(density: DisplayDensity): void {
    this.sessionService.setItem(StorageKey.displayDensity, density);
    this.apply(density);
    this.density$.next(density);
  }

  private readStoredDensity(): DisplayDensity {
    const stored = this.sessionService.getItem(StorageKey.displayDensity);
    return stored && MAX_WIDTHS[stored as DisplayDensity] ? (stored as DisplayDensity) : 'wide';
  }

  private apply(density: DisplayDensity): void {
    document.documentElement.style.setProperty('--detail-page-max-width', MAX_WIDTHS[density]);
  }
}
